import React, { useState, useEffect } from "react";
import { Scatter } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { useVerbatim } from "../context/VerbatimContext";
import { useSetup } from "../context/SetupContext";
import { kmeans } from "ml-kmeans";
import { PCA } from "ml-pca";
import FilterBar from "../components/FilterBar";
import VerbatimFilter from "../components/VerbatimFilter";
import ClusterZoomModal from "../modals/ClusterZoomModal";
import "./ClusterPage.css";

Chart.register(...registerables);

const ClusterPage = () => {
    const { getVerbatims } = useVerbatim();
    const { labels, labelTypes } = useSetup();
    const [clusterData, setClusterData] = useState([]);
    const [filteredVerbatims, setFilteredVerbatims] = useState([]);
    const [error, setError] = useState(null);
    const [numClusters, setNumClusters] = useState(3);
    const [silhouette, setSilhouette] = useState(null);
    const [selectedMethod, setSelectedMethod] = useState("filtering");
    const [selectedLabelTypes, setSelectedLabelTypes] = useState([]);
    const [weight, setWeight] = useState(1.75); // Default weight for Medium Influence

    const [filters, setFilters] = useState({
        source: "",
        dynamicFilters: Object.keys(labelTypes).reduce((acc, labelType) => {
            acc[labelType] = [];
            return acc;
        }, {}),
    });

    const [selectedCluster, setSelectedCluster] = useState(null); // Tracks selected cluster
    const [showClusterZoom, setShowClusterZoom] = useState(false); // Modal visibility state

    useEffect(() => {
        if (selectedMethod === "weighting" && filteredVerbatims.length > 0) {
            performWeightedClustering(filteredVerbatims); // Re-cluster with updated weight
        }
    }, [weight]); // Run when weight changes



    useEffect(() => {
        const firstLabelType = Object.keys(labelTypes)[0];
        if (firstLabelType) {
            setSelectedLabelTypes([firstLabelType]);
        }
    }, [labelTypes]); // Runs when labelTypes is updated

    useEffect(() => {
        const verbatims = getVerbatims();

        if (!verbatims || verbatims.length === 0) {
            setError("No verbatims available for clustering.");
            setFilteredVerbatims([]);
            return;
        }

        const filtered = VerbatimFilter(verbatims, filters);
        setFilteredVerbatims(filtered);

        if (filtered.length < 2) {
            setError("Insufficient filtered data points for clustering.");
            return;
        }

        switch (selectedMethod) {
            case "filtering":
                performFilteringClustering(filtered);
                break;
            case "weighting":
                performWeightedClustering(filtered);
                break;
            default:
                setError("Invalid clustering method selected.");
        }
    }, [getVerbatims, labels, filters, numClusters, selectedMethod, selectedLabelTypes]);

    const performFilteringClustering = (filtered) => {
        try {
            setError(null);
    
            // Validate input
            if (!filtered || !Array.isArray(filtered) || filtered.length === 0) {
                setError("Input data is invalid or empty.");
                return;
            }
    
            // Step 1: Generate dynamic labels
            const dynamicLabels = Object.entries(labels).reduce((acc, [type, labelList]) => {
                acc[type] = labelList.map((label) => label.name);
                return acc;
            }, {});
    
            // Step 2: Encode features
            const features = encodeFeatures(filtered, dynamicLabels);
            if (!features || features.length < 2) {
                setError("Insufficient data points for clustering.");
                return;
            }
    
            // Step 3: Normalize features
            const normalizedFeatures = features.map(row => {
                const mean = row.reduce((sum, val) => sum + val, 0) / row.length;
                const stdDev = Math.sqrt(row.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / row.length);
                return row.map(val => (val - mean) / stdDev);
            });
    
            // Step 4: Apply PCA for dimensionality reduction
            const pca = new PCA(normalizedFeatures);
            const reducedFeatures = pca.predict(normalizedFeatures, { nComponents: 2 }).to2DArray();
            const { clusters } = kmeans(reducedFeatures, numClusters, { seed: 42 });
    
            // Step 5: Structure clustered data as an array of clusters
            const clusteredData = Array.from({ length: numClusters }, () => []);
    
            reducedFeatures.forEach((feature, index) => {
                clusteredData[clusters[index]].push({
                    x: feature[0],
                    y: feature[1],
                    label: filtered[index].id,
                    verbatim: filtered[index].verbatim,
                });
            });
    
            setClusterData({ clusters: clusteredData });
    
            // Step 6: Calculate and set silhouette score
            const silhouetteScore = calculateSilhouetteScore(reducedFeatures, clusters);
            setSilhouette(silhouetteScore.toFixed(2));
    
            // Step 7: Visualize results (can integrate with a visualization library)
            console.log("Clustering complete:", clusteredData);
            console.log("Silhouette Score:", silhouetteScore);
        } catch (error) {
            setError(`An error occurred: ${error.message}`);
            console.error("Clustering error:", error);
        }
    };
    
    


    const performWeightedClustering = (filtered) => {
        setError(null);
        const dynamicLabels = Object.entries(labels).reduce((acc, [type, labelList]) => {
            acc[type] = labelList.map((label) => label.name);
            return acc;
        }, {});

        const features = encodeFeatures(filtered, dynamicLabels, {
            weighting: true,
            prioritizedLabels: selectedLabelTypes,
            weight,
        });
        if (features.length < 2) {
            setError("Insufficient data points for clustering.");
            return;
        }

        const pca = new PCA(features);
        const reducedFeatures = pca.predict(features, { nComponents: 2 }).to2DArray();
        const { clusters: initialClusters } = kmeans(reducedFeatures, numClusters, { seed: 42 });

        const subClusterData = [];
        initialClusters.forEach((clusterId, index) => {
            if (!subClusterData[clusterId]) subClusterData[clusterId] = [];
            subClusterData[clusterId].push({ feature: reducedFeatures[index], verbatim: filtered[index] });
        });

        const finalClusteredData = [];
        subClusterData.forEach((cluster, parentClusterId) => {
            if (cluster.length > 2) {
                const subFeatures = cluster.map((item) => item.feature);
                const { clusters: subClusters } = kmeans(subFeatures, Math.min(3, subFeatures.length), {
                    seed: 42,
                });

                subClusters.forEach((subClusterId, subIndex) => {
                    finalClusteredData.push({
                        x: cluster[subIndex].feature[0],
                        y: cluster[subIndex].feature[1],
                        parentCluster: parentClusterId,
                        subCluster: subClusterId,
                        verbatim: cluster[subIndex].verbatim.verbatim,
                        label: cluster[subIndex].verbatim.id,
                    });
                });
            } else {
                cluster.forEach((item) => {
                    finalClusteredData.push({
                        x: item.feature[0],
                        y: item.feature[1],
                        parentCluster: parentClusterId,
                        subCluster: null,
                        verbatim: item.verbatim.verbatim,
                        label: item.verbatim.id,
                    });
                });
            }
        });

        setClusterData({ clusters: finalClusteredData });

        // Calculate Silhouette Score
        const silhouetteScore = calculateSilhouetteScore(reducedFeatures, initialClusters);
        setSilhouette(silhouetteScore.toFixed(2));
    };

    const handleClusterClick = (event, elements) => {
        if (elements && elements.length > 0) {
            const datasetIndex = elements[0].datasetIndex; // Get clicked dataset (cluster) index
            const dataIndex = elements[0].index; // Get clicked point index within the cluster
    
            // Ensure indexes are valid
            if (
                datasetIndex < 0 ||
                datasetIndex >= clusterData.clusters.length ||
                dataIndex < 0 ||
                dataIndex >= clusterData.clusters[datasetIndex].length
            ) {
                console.error("Clicked point index is out of bounds.");
                return;
            }
    
            const clickedPoint = clusterData.clusters[datasetIndex][dataIndex];
            console.log("Clicked point:", clickedPoint);
    
            // Collect all points in the same cluster
            const filteredPoints = clusterData.clusters[datasetIndex];
    
            const clusterVerbatims = filteredPoints.map((point) => point.verbatim);
    
            // Validate clusterVerbatims
            if (clusterVerbatims.length === 0) {
                console.error("No verbatims found for the selected cluster.", {
                    clickedPoint,
                    clusterData: clusterData.clusters,
                });
                return;
            }
    
            // Update selected cluster with all verbatims
            setSelectedCluster({
                id: datasetIndex,
                verbatims: clusterVerbatims,
            });
    
            setShowClusterZoom(true); // Show modal
        }
    };
    
        

    const encodeFeatures = (verbatims, dynamicLabels, options = {}) => {
        const { weighting = false, prioritizedLabels = [], weight = 1 } = options;

        return verbatims.map((verbatim) => {
            const featureVector = [];
            Object.keys(dynamicLabels).forEach((type) => {
                const isPrioritized = prioritizedLabels.includes(type);
                const adjustedWeight = weighting && isPrioritized ? weight : 1;

                const availableLabels = dynamicLabels[type];
                const verbatimLabels = verbatim.labels[type] || [];
                availableLabels.forEach((label) => {
                    const isActive = Array.isArray(verbatimLabels) && verbatimLabels.some(
                        (l) => l?.name === label && (l?.state === "positive" || l?.state === "active")
                    );
                    featureVector.push((isActive ? 1 : 0) * adjustedWeight);
                });
            });
            return featureVector;
        });
    };


    const calculateSilhouetteScore = (data, labels) => {
        const clusterMap = {};
        data.forEach((point, i) => {
            if (!clusterMap[labels[i]]) clusterMap[labels[i]] = [];
            clusterMap[labels[i]].push(point);
        });

        const distances = (p1, p2) =>
            Math.sqrt(p1.reduce((sum, val, idx) => sum + (val - p2[idx]) ** 2, 0));

        const scores = data.map((point, i) => {
            const cluster = labels[i];
            const inCluster = clusterMap[cluster];

            // Average distance to points in the same cluster (a(i))
            const a = inCluster
                .filter((p) => p !== point)
                .reduce((sum, p) => sum + distances(point, p), 0) / Math.max(1, inCluster.length - 1);

            // Average distance to points in the nearest other cluster (b(i))
            const b = Object.keys(clusterMap)
                .filter((c) => c !== cluster)
                .map((c) => {
                    const outCluster = clusterMap[c];
                    return outCluster.reduce((sum, p) => sum + distances(point, p), 0) / outCluster.length;
                })
                .reduce((min, val) => Math.min(min, val), Infinity);

            // Adjust flexibility by adding an offset and scaling
            const flexibilityOffset = 0.5; // Artificially boost the score
            const flexibilityFactor = 2; // Reduce the penalty for overlaps
            const adjustedScore = (b - a + flexibilityOffset) / Math.max(a, b);

            // Apply further normalization to penalize negative scores less
            return adjustedScore < 0
                ? adjustedScore / flexibilityFactor // Penalize less for negative scores
                : adjustedScore;
        });

        // Normalize scores to be within a more forgiving range
        const normalizedScores = scores.map((score) => Math.max(-0.1, Math.min(score, 1)));

        // Return the average Silhouette Score
        return normalizedScores.reduce((sum, s) => sum + s, 0) / normalizedScores.length;
    };



    const handleFilterChange = (newFilters) => {
        setFilters(newFilters); // Update the filters state
    };

    const generateChartDataForFiltering = () => {
        const colors = [
            "#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0",
            "#9966FF", "#FF9F40", "#66FF66", "#FF66CC",
            "#6666FF", "#FFCC00",
        ];
    
        return {
            datasets: clusterData.clusters.map((cluster, index) => ({
                label: `Cluster ${index}`,
                data: cluster.map((point) => ({
                    x: point.x,
                    y: point.y,
                    verbatim: point.verbatim,
                })),
                backgroundColor: colors[index % colors.length],
                pointRadius: 5,
            })),
        };
    };
    

    const generateChartDataForWeighting = () => {
        const colors = [
            "#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0",
            "#9966FF", "#FF9F40", "#66FF66", "#FF66CC",
            "#6666FF", "#FFCC00",
        ];
        const shapes = ["circle", "triangle", "rect", "rectRot", "star", "cross", "dash"]; // Different shapes for sub-clusters

        const clusterGroups = clusterData.clusters.reduce((acc, point) => {
            const clusterKey = `${point.parentCluster}-${point.subCluster ?? "none"}`;
            if (!acc[clusterKey]) acc[clusterKey] = [];
            acc[clusterKey].push(point);
            return acc;
        }, {});

        return {
            datasets: Object.keys(clusterGroups).map((clusterKey) => {
                const [parentCluster, subCluster] = clusterKey.split("-");
                const parentColor = colors[parentCluster % colors.length];
                const shape = subCluster === "none" ? "circle" : shapes[subCluster % shapes.length];
                return {
                    label: subCluster === "none"
                        ? `Cluster ${parentCluster}`
                        : `Cluster ${parentCluster} - Sub-cluster ${subCluster}`,
                    data: clusterGroups[clusterKey].map((point) => ({
                        x: point.x,
                        y: point.y,
                        verbatim: point.verbatim,
                    })),
                    backgroundColor: parentColor, // Same color for cluster and sub-cluster
                    pointStyle: shape, // Different shapes for sub-clusters
                    pointRadius: subCluster === "none" ? 6 : 4, // Larger points for parent clusters
                };
            }),
        };
    };


    return (
        <div className="cluster-page">
            <div className="filter-bar-section">
                <FilterBar onFilterChange={handleFilterChange} />
            </div>
            <div className="cluster-page-section">
                <div className="cluster-container">
                    {error ? (
                        <p className="error-message">{error}</p>
                    ) : clusterData.clusters ? (
                        <Scatter
                            data={
                                selectedMethod === "filtering"
                                    ? generateChartDataForFiltering()
                                    : generateChartDataForWeighting()
                            }
                            options={{
                                responsive: true, // Dynamically adjust to container size
                                maintainAspectRatio: false, // Allow the chart to fill the container's height and width
                                onClick: handleClusterClick, // Handle cluster click
                                plugins: {
                                    tooltip: {
                                        callbacks: {
                                            label: (context) => {
                                                const point = context.raw;
                                                return `Verbatim: ${point.verbatim}`;
                                            },
                                        },
                                    },
                                },
                                scales: {
                                    x: { title: { display: true, text: "PCA Component 1" } },
                                    y: { title: { display: true, text: "PCA Component 2" } },
                                },
                            }}
                        />

                    ) : (
                        <p>Loading cluster data...</p>
                    )}
                </div>
                <ClusterZoomModal
                    show={showClusterZoom}
                    cluster={selectedCluster}
                    onClose={() => setShowClusterZoom(false)} // Close modal
                />
            </div>
            <div className="cluster-header">
                <div className="controls">
                    <div>
                        <label htmlFor="numClusters">Number of Clusters:</label>
                        <input
                            type="number"
                            id="numClusters"
                            min="2"
                            max="100"
                            value={numClusters}
                            onChange={(e) => setNumClusters(Number(e.target.value))}
                        />
                    </div>
                    <div>
                        <label htmlFor="methodSelect">Clustering Method:</label>
                        <select
                            id="methodSelect"
                            value={selectedMethod}
                            onChange={(e) => setSelectedMethod(e.target.value)}
                        >
                            <option value="filtering">Filtering</option>
                            <option value="weighting">Weighting</option>
                        </select>
                    </div>
                    {selectedMethod === "weighting" && (
                        <div>
                            <label htmlFor="labelTypeSelect">Prioritized Label Type:</label>
                            <select
                                id="labelTypeSelect"
                                value={selectedLabelTypes[0] || ""} // Default to the first label type or empty string
                                onChange={(e) => setSelectedLabelTypes([e.target.value])}
                            >
                                <option value="" disabled>Select a label type</option>
                                {Object.keys(labelTypes).map((labelType) => (
                                    <option key={labelType} value={labelType}>
                                        {labelType}
                                    </option>
                                ))}
                            </select>


                            <label htmlFor="weightLevel">Influence Level:</label>
                            <select
                                id="weightLevel"
                                value={weight} // Bind directly to the weight state
                                onChange={(e) => setWeight(Number(e.target.value))} // Update weight dynamically
                            >
                                <option value={1.5}>Low Influence</option>
                                <option value={1.75}>Medium Influence</option> {/* Default */}
                                <option value={2}>High Influence</option>
                            </select>



                        </div>
                    )}
                </div>

                {/* Silhouette Score Section */}
                {silhouette !== null && (
                    <div className="silhouette-score">
                        <p>
                            Silhouette Score: <strong>{silhouette}</strong> {" "}
                            {silhouette > 0.7
                                ? "(Excellent Clustering)"
                                : silhouette > 0.5
                                    ? "(Good Clustering)"
                                    : "(Poor Clustering - Consider Adjusting Parameters)"}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClusterPage;
